import React from "react";
import Logo from "../components/Logo";
import { Link } from "react-router-dom";
import {
  Wind as Wing,
  Heart,
  Sword,
  Eye,
  Mountain,
  Feather,
  Zap,
} from "lucide-react";

interface LandingPageProps {
  onStartTest: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onStartTest }) => {

  const handleScrollToTest = () => {
    onStartTest(); // Directly start the test
  }

  return (
    <>
      {/* Language Switcher */}
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
        <div className="flex justify-end pt-4 pr-4">
          <a
            href="#"
            className="inline-flex items-center px-4 py-2 mr-2 text-sm bg-orange-500 border border-orange-500 text-white rounded-lg shadow font-semibold hover:bg-orange-600 transition"
            style={{ pointerEvents: 'none', opacity: 1 }}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 0v20m0-20C7.03 2 2 7.03 2 12h20c0-4.97-5.03-10-10-10z" />
            </svg>
            Español
          </a>
          <a
            href="https://latinocareertest.com/"
            className="inline-flex items-center px-4 py-2 bg-white text-sm border border-orange-400 text-orange-600 rounded-lg shadow font-semibold hover:bg-orange-50 transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 0v20m0-20C7.03 2 2 7.03 2 12h20c0-4.97-5.03-10-10-10z" />
            </svg>
            English
          </a>
        </div>
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <header className="flex justify-center mb-12">
            <Logo size="large" />
          </header>

          <section className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              Encuentra tu Trayectoria Profesional Ideal en 15 Minutos - Gratis
            </h1>
            <p className="text-xl text-gray-600 mb-4 leading-relaxed">
              La primera evaluación profesional y de personalidad diseñada para Latinos - basada en cultura, valores y fortalezas.
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Descubre tu Arquetipo Latino™ y cómo tu trasfondo cultural moldea tu estilo de trabajo y potencial profesional.
            </p>

            <button 
              onClick={handleScrollToTest}
              className="w-full bg-orange-500 text-white py-6 px-8 rounded-lg mb-8 transform hover:scale-105 transition-transform duration-300 shadow-lg"
            >
              <h2 className="text-2xl font-bold">Comenzar Evaluación Gratuita Ahora</h2>
            </button>

            <div className="p-4 bg-orange-100 rounded-lg text-orange-800 inline-block">
              <p className="text-sm">
                Esta evaluación toma aproximadamente 15 minutos
              </p>
            </div>

            {/* <div className="mt-4 text-gray-600">
              <p>
                Puedes hacer esta evaluación múltiples veces para seguir tu crecimiento profesional.
                <br />
                Cada intento se guarda por separado en tu perfil.
              </p>
            </div> */}
          </section>

          {/* Remove the registration form section - it was here */}

          <section className="mb-12">
            <p className="text-center text-lg text-gray-700 mb-8">
              El Latino Career Test™ es una herramienta de autoconciencia con base cultural para la orientación profesional.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 text-center mb-6">
              Los Siete Arquetipos Latinos™
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* First row - 4 archetypes */}
              <div className="bg-white p-4 rounded-lg shadow-md text-center">
                <div className="h-12 w-12 bg-[#c62828] rounded-full flex items-center justify-center mx-auto mb-3">
                  <Sword size={24} className="text-white" />
                </div>
                <h3 className="font-semibold mb-1">Jaguar</h3>
                <p className="text-sm text-gray-600">El triunfador decisivo</p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-md text-center">
                <div className="h-12 w-12 bg-[#37474f] rounded-full flex items-center justify-center mx-auto mb-3">
                  <Wing size={24} className="text-white" />
                </div>
                <h3 className="font-semibold mb-1">Cóndor</h3>
                <p className="text-sm text-gray-600">
                  El estratega visionario
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-md text-center">
                <div className="h-12 w-12 bg-[#6d4c41] rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mountain size={24} className="text-white" />
                </div>
                <h3 className="font-semibold mb-1">Llama</h3>
                <p className="text-sm text-gray-600">El colaborador leal</p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-md text-center">
                <div className="h-12 w-12 bg-[#8e24aa] rounded-full flex items-center justify-center mx-auto mb-3">
                  <Feather size={24} className="text-white" />
                </div>
                <h3 className="font-semibold mb-1">Quetzal</h3>
                <p className="text-sm text-gray-600">El creativo auténtico</p>
              </div>

              {/* Second row - 3 archetypes centered */}
              <div className="md:col-span-4 flex justify-center gap-4 flex-wrap">
                <div className="bg-white p-4 rounded-lg shadow-md text-center w-full md:w-1/4">
                  <div className="h-12 w-12 bg-[#0288d1] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Zap size={24} className="text-white" />
                  </div>
                  <h3 className="font-semibold mb-1">Colibrí</h3>
                  <p className="text-sm text-gray-600">La chispa alegre</p>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-md text-center w-full md:w-1/4">
                  <div className="h-12 w-12 bg-[#00796b] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Heart size={24} className="text-white" />
                  </div>
                  <h3 className="font-semibold mb-1">Coquí</h3>
                  <p className="text-sm text-gray-600">
                    El defensor de la comunidad
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-md text-center w-full md:w-1/4">
                  <div className="h-12 w-12 bg-[#455a64] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Eye size={24} className="text-white" />
                  </div>
                  <h3 className="font-semibold mb-1">Búho</h3>
                  <p className="text-sm text-gray-600">
                    El planificador analítico
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-12 text-gray-800">
              <p className="font-medium mb-8">Descubre tu Arquetipo Latino™ realizando esta <a href="#" onClick={handleScrollToTest} className="text-orange-500 hover:text-orange-600">evaluación gratuita</a></p>
              <p className="font-medium mb-8">
                El Test Latino™ fue creado por{" "}
                <a
                  href="https://www.linkedin.com/in/josepinero"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-500 hover:text-orange-600"
                >José Piñero</a>, El Coach #1 para Latinos
              </p>
              <img src="/Jose Pinero - Times Square billboard.jpg" alt="Jose Pinero - Times Square billboard" className="mb-8 rounded-lg shadow-2xl" />
              <p className="font-medium">
                José Piñero es ampliamente reconocido como el coach #1 de carrera y liderazgo para profesionales hispanos y latinos. Con más de 20 años de experiencia en liderazgo corporativo, incluyendo roles ejecutivos en Microsoft y Procter & Gamble, y más de una década de coaching, José ha ayudado a miles de profesionales a alcanzar sus metas. Marcas globales como Google, Amazon, Microsoft, Netflix, Nike, Ford y más confían en Piñero. La misión de José es simple: educar, empoderar y elevar a un millón de latinos, convirtiendo las fortalezas culturales en éxito profesional. Destacado en CNN, Forbes y People en Español, José aporta estrategias comprobadas, comprensión cultural y resultados del mundo real para ayudarte a obtener promociones, ganar más y liderar con confianza.
              </p>
              <button onClick={handleScrollToTest} className="bg-orange-500 text-white py-5 px-7 rounded-lg mt-8 transform hover:scale-105 transition-transform duration-300 shadow-lg">
                <h2 className="text-2xl font-bold">Haz la Evaluación - Es Gratuita</h2>
              </button>
            </div>
          </section>

          <footer className="text-center text-gray-500 text-sm border-t border-gray-200 pt-8">
            <p className="mb-4">
              El Test Latino™ está basado en investigación sobre el desarrollo profesional latino y valores culturales.
              <br />
              Diseñado para ayudar a los Latinos a aprovechar sus fortalezas culturales en entornos profesionales.
            </p>
            <div className="flex items-center justify-center mb-6">
              <Heart size={16} className="text-orange-500 mr-1" />
              <span>Hecho con amor para la comunidad profesional latina</span>
            </div>
            <div className="text-xs text-gray-400 space-y-2">
              <p>© 2025 YOJ Inc. Todos los Derechos Reservados.</p>
              <p>Test Latino™ es una marca registrada de YOJ Inc. Todas las demás marcas registradas son propiedad de sus respectivos propietarios.
              </p>
              <p>El Test Latino™ es propiedad intelectual de YOJ Inc. y fue desarrollado por José Piñero, ampliamente reconocido como el Principal Coach para Latinos. Latino Leadership Performance es una división comercial de YOJ Inc.
              </p>
              <p>La reproducción, distribución o uso no autorizado de esta evaluación, sus preguntas, arquetipos o contenido relacionado está estrictamente prohibido y protegido bajo las leyes de derechos de autor y marcas registradas de EE.UU.
              </p>
              <p className="mt-4">
                <Link
                  to="/legal"
                  className="text-orange-500 hover:text-orange-600 underline"
                >
                  Política de Privacidad y Términos y Condiciones
                </Link>
              </p>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
};

export default LandingPage;
